import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Plus, Pencil, Trash2, FileText, Check } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { TEMPLATE_SECTIONS, AFWERKING_PROFILES, sectionCounts, sectionSummary, profileSegments } from '../utils/template-sections.js';

/**
 * Overzicht van de boringsjablonen. Aanmaken/bewerken gebeurt op een eigen pagina
 * (<template-editor>, routes /templates/new en /templates/:id).
 *
 * Een sjabloon is een set default-waarden per onderdeel (project, boring, grondlaag, monster,
 * peilbuis, watermonster, afwerking) die een bedrijfsbeheerder vastlegt; die worden automatisch
 * voor-ingevuld bij projectaanmaak en bij het digitaliseren. Per sjabloon een kaart met per
 * onderdeel wat er is ingesteld, zodat je zonder openen ziet wat een sjabloon doet.
 */
export class TemplatesPage extends LitElement {
  static styles = css`
    :host { display:block; max-width:1120px; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; gap:24px; flex-wrap:wrap; }
    h1 { margin:0 0 8px; font-size:1.875rem; font-weight:700; letter-spacing:-0.03em; color:var(--mv-text); }
    .intro { max-width:70ch; color:var(--mv-text-sub); font-size:0.875rem; line-height:1.6; margin:0; }
    .btn-primary { display:inline-flex; align-items:center; gap:6px; background:var(--mv-primary); color:#fff; border:none; border-radius:9px; padding:10px 16px; font:inherit; font-size:0.875rem; font-weight:600; cursor:pointer; }
    .btn-primary:hover { background:var(--mv-primary-hover, var(--mv-primary)); }
    .toast { margin-top:20px; display:flex; align-items:center; gap:8px; background:var(--mv-primary-tint); color:var(--mv-primary); font-size:0.8125rem; font-weight:600; padding:10px 14px; border-radius:9px; width:fit-content; }
    .list { display:flex; flex-direction:column; gap:16px; margin-top:24px; }
    .card { background:var(--mv-surface); border:1px solid var(--mv-border-soft, var(--mv-border)); border-radius:14px; box-shadow:0 5px 15px rgba(0,0,0,.02); cursor:pointer; overflow:hidden; transition:border-color .15s, box-shadow .15s; }
    .card:hover { border-color:var(--mv-primary-light, var(--mv-primary)); box-shadow:0 8px 24px rgba(0,0,0,.05); }
    .card-head { display:flex; align-items:center; gap:14px; padding:18px 20px; border-bottom:1px solid var(--mv-border-soft, var(--mv-border)); }
    .icon { width:38px; height:38px; border-radius:9px; background:var(--mv-primary-tint); color:var(--mv-primary); display:flex; align-items:center; justify-content:center; flex:none; }
    .titles { flex:1; min-width:0; }
    .name { font-size:0.97rem; font-weight:700; letter-spacing:-0.02em; color:var(--mv-text); }
    .meta { font-size:0.78rem; color:var(--mv-text-sub); margin-top:2px; }
    .icon-btn { border:1px solid var(--mv-border-soft, var(--mv-border)); background:var(--mv-surface); border-radius:8px; width:34px; height:34px; display:flex; align-items:center; justify-content:center; color:var(--mv-text-sub); cursor:pointer; flex:none; }
    .icon-btn:hover { color:var(--mv-text); border-color:var(--mv-border); }
    .icon-btn.danger:hover { color:var(--mv-danger, #f1416c); border-color:var(--mv-danger, #f1416c); }
    .tiles { display:grid; grid-template-columns:repeat(auto-fill, minmax(230px, 1fr)); }
    .tile { padding:14px 20px 16px; border-right:1px solid var(--mv-border-soft, var(--mv-border)); border-bottom:1px solid var(--mv-border-soft, var(--mv-border)); display:flex; flex-direction:column; gap:6px; min-width:0; }
    .tile-head { display:flex; align-items:center; gap:7px; }
    .dot { width:6px; height:6px; border-radius:50%; background:var(--mv-border); }
    .dot.on { background:var(--mv-primary); }
    .tile-title { font-size:0.7rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--mv-text-sub); }
    .values { font-size:0.8125rem; line-height:1.5; color:var(--mv-text); }
    .values.none { color:var(--mv-text-muted); }
    .afw { display:flex; align-items:center; gap:8px; font-size:0.78rem; color:var(--mv-text-sub); }
    .afw .l { width:100px; flex:none; white-space:nowrap; }
    .afw .track { flex:1; height:8px; border-radius:4px; background:var(--mv-surface-alt); position:relative; overflow:hidden; }
    .afw .track span { position:absolute; top:0; bottom:0; }
    .empty { margin-top:24px; padding:32px; border:1px dashed var(--mv-border); border-radius:14px; text-align:center; color:var(--mv-text-sub); }
    .error { color:var(--mv-danger, #d33); font-size:0.85rem; margin:8px 0; }
  `;

  static properties = {
    templates: { state: true },
    loading: { state: true },
    error: { state: true },
    _toast: { state: true },
  };

  constructor() {
    super();
    this.templates = [];
    this.loading = true;
    this.error = '';
    this._toast = '';
  }

  connectedCallback() {
    super.connectedCallback();
    // De editor stuurt na opslaan de naam mee in de geschiedenis (zie template-editor _save).
    const saved = window.history.state?.savedTemplate;
    if (saved) {
      this._toast = `Sjabloon „${saved}” opgeslagen`;
      window.history.replaceState({}, '', window.location.pathname);
    }
    this.loadTemplates();
  }

  get _canManage() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  async loadTemplates() {
    this.loading = true;
    try {
      this.templates = await api.getTemplates();
    } catch (e) {
      this.error = `Kon sjablonen niet laden: ${e.message}`;
    } finally {
      this.loading = false;
    }
  }

  _navigate(path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async deleteTemplate(tpl) {
    const ok = await confirmDialog({
      title: 'Sjabloon verwijderen',
      message: `Weet je zeker dat je sjabloon "${tpl.name}" wilt verwijderen?`,
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteTemplate(tpl.id);
      this._toast = '';
      await this.loadTemplates();
    } catch (e) {
      this.error = `Fout bij verwijderen: ${e.message}`;
    }
  }

  render() {
    return html`
      <div class="header">
        <div>
          <h1>Boringsjablonen</h1>
          <p class="intro">
            Met boringsjablonen legt een bedrijfsbeheerder standaardwaarden vast die automatisch worden
            voor-ingevuld bij het aanmaken van een project en bij het digitaliseren. Zo bespaar je tijd en
            voorkom je fouten.
          </p>
        </div>
        ${this._canManage ? html`
          <button class="btn-primary" @click=${() => this._navigate('/templates/new')}>
            ${unsafeSVG(lucideIcon(Plus, 16))} Nieuw sjabloon
          </button>` : ''}
      </div>

      ${this._toast ? html`<div class="toast">${unsafeSVG(lucideIcon(Check, 15))} ${this._toast}</div>` : ''}
      ${this.error ? html`<div class="error">${this.error}</div>` : ''}

      ${this.loading ? html`<div class="empty">Laden…</div>` : (
        this.templates.length === 0
          ? html`<div class="empty">
              ${this._canManage
                ? 'Er zijn nog geen sjablonen. Maak er een aan om velden automatisch te laten invullen.'
                : 'Er zijn nog geen sjablonen. Vraag een beheerder om er een aan te maken.'}
            </div>`
          : html`<div class="list">${this.templates.map((t) => this._renderCard(t))}</div>`
      )}
    `;
  }

  _renderCard(t) {
    const d = t.defaults || {};
    const counts = TEMPLATE_SECTIONS.map((s) => sectionCounts(d, s));
    const filled = counts.filter((c) => c.set).length;
    // Afwerking telt in profielen, niet in standaardwaarden.
    const values = counts.slice(0, -1).reduce((n, c) => n + c.set, 0);
    const open = () => this._navigate(`/templates/${t.id}`);
    return html`
      <div class="card" @click=${open}>
        <div class="card-head">
          <span class="icon">${unsafeSVG(lucideIcon(FileText, 19))}</span>
          <div class="titles">
            <div class="name">${t.name}</div>
            <div class="meta">${filled} van ${TEMPLATE_SECTIONS.length} onderdelen ingesteld · ${values} ${values === 1 ? 'standaardwaarde' : 'standaardwaarden'}</div>
          </div>
          ${this._canManage ? html`
            <button class="icon-btn" title="Bewerken" @click=${(e) => { e.stopPropagation(); open(); }}>${unsafeSVG(lucideIcon(Pencil, 15))}</button>
            <button class="icon-btn danger" title="Verwijderen" @click=${(e) => { e.stopPropagation(); this.deleteTemplate(t); }}>${unsafeSVG(lucideIcon(Trash2, 15))}</button>
          ` : ''}
        </div>
        <div class="tiles">
          ${TEMPLATE_SECTIONS.map((sec, i) => {
            const summary = sectionSummary(d, sec);
            return html`
              <div class="tile">
                <div class="tile-head"><span class="dot ${counts[i].set ? 'on' : ''}"></span><span class="tile-title">${sec.title}</span></div>
                ${sec.id === 'afwerking'
                  ? AFWERKING_PROFILES.map(([w, l]) => html`
                      <div class="afw"><span class="l">${l}</span>
                        <div class="track">${profileSegments(d.afwerking?.[w]).map((g) => html`<span style="left:${g.start}%; width:${g.size}%; background:${g.color};"></span>`)}</div>
                      </div>`)
                  : html`<div class="values ${summary ? '' : 'none'}">${summary || 'Geen defaults'}</div>`}
              </div>`;
          })}
        </div>
      </div>`;
  }
}

customElements.define('templates-page', TemplatesPage);
